// components/formDialog/StudentDialog.tsx
import InputField from "@/components/form/components/InputField";
import NumberField from "@/components/form/components/NumberField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateBatchStudentMutation,
} from "@/hooks/api/student.queries";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type StudentDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  /** The batch ID to enroll the student into. Silently injected into the form. */
  batchId: string;
};

const studentSchema = z.object({
  name: z.string().min(1, "Name is required"),
  org_email: z.email("Invalid organizational email"),
  personal_email: z.email("Invalid personal email"),
  phone: z.string().min(1, "Phone number is required"),
  roll_number: z.string().min(1, "Roll number is required"),
  cgpa: z.preprocess((val) => Number(val), z.number().min(0).max(10, "CGPA must be between 0 and 10")),
});

type StudentFormType = {
  name: string;
  org_email: string;
  personal_email: string;
  phone: string;
  roll_number: string;
  cgpa: number;
};

const StudentDialog = ({
  isOpen,
  onOpenChange,
  onCancel,
  batchId,
}: StudentDialogProps) => {
  const createMutation = useCreateBatchStudentMutation(batchId);
  const isPending = createMutation.isPending;

  const defaultValues: StudentFormType = {
    name: "",
    org_email: "",
    personal_email: "",
    phone: "",
    roll_number: "",
    cgpa: 0,
  };

  const form = useForm<StudentFormType>({
    resolver: zodResolver(studentSchema) as any,
    defaultValues,
  });

  useEffect(() => {
    if (isOpen) form.reset(defaultValues);
  }, [isOpen, form]);

  const handleSubmit = (values: StudentFormType) => {
    createMutation.mutate(
      { ...values, batch_id: batchId },
      { onSuccess: onCancel }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Add New Student</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            <InputField<StudentFormType>
              name="name"
              label="Full Name"
              placeholder="e.g. Jane Doe"
              type="text"
              disabled={isPending}
              required
            />
            <InputField<StudentFormType>
              name="roll_number"
              label="Roll Number"
              placeholder="e.g. CSE2021001"
              type="text"
              disabled={isPending}
              required
            />
            <InputField<StudentFormType>
              name="org_email"
              label="Organizational Email"
              placeholder="e.g. jane.doe@college.edu"
              type="email"
              disabled={isPending}
              required
            />
            <InputField<StudentFormType>
              name="personal_email"
              label="Personal Email"
              placeholder="e.g. jane.doe@gmail.com"
              type="email"
              disabled={isPending}
              required
            />
            <InputField<StudentFormType>
              name="phone"
              label="Phone Number"
              placeholder="e.g. +91-9876543210"
              type="tel"
              disabled={isPending}
              required
            />
            <NumberField<StudentFormType>
              name="cgpa"
              label="CGPA"
              placeholder="e.g. 8.5"
              disabled={isPending}
              required
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Adding..." : "Add Student"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default StudentDialog;
